import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth, type User } from "../context/AuthProvider";
import { authFetch } from "../lib/auth";
import { accountIdentity } from "../lib/accountIdentity";

type Status = { key: string; account: User | null; loading: boolean; error: boolean };

/** Only a fresh /api/me response for the signed-in account may decide verification. */
export function useVerificationStatus({ enabled = true, watchSearch = true }: { enabled?: boolean; watchSearch?: boolean } = {}) {
  const { user, ready } = useAuth();
  const location = useLocation();
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<Status>({ key: "", account: null, loading: true, error: false });
  const key = JSON.stringify([enabled, ready, user?.id, user?.is_admin, user?.verification_exempt, user?.ekyc_status, user?.account_status, user?.is_social, user?.social_provider, user?.social_id, location.pathname, watchSearch ? location.search : "", watchSearch ? location.hash : "", attempt]);
  const accountId = accountIdentity(user?.id);

  useEffect(() => {
    if (!enabled || !ready || accountId === null) return;
    let active = true;
    const controller = new AbortController();
    setStatus({ key, account: null, loading: true, error: false });
    void (async () => {
      try {
        const response = await authFetch("/api/me", { signal: controller.signal });
        const payload: unknown = await response.json();
        const body = payload && typeof payload === "object" ? payload as Record<string, unknown> : null;
        const current = body?.user && typeof body.user === "object" ? body.user as Record<string, unknown> : null;
        const validStatus = (field: string) => current && Object.prototype.hasOwnProperty.call(current, field) && (current[field] === null || typeof current[field] === "string");
        if (!response.ok || body?.ok !== true || !current || accountIdentity(current.id) !== accountId || !validStatus("ekyc_status") || !validStatus("account_status")) throw new Error("verification status unavailable");
        if (active) setStatus({ key, account: current as User, loading: false, error: false });
      } catch {
        if (active) setStatus({ key, account: null, loading: false, error: true });
      }
    })();
    return () => { active = false; controller.abort(); };
  }, [accountId, enabled, ready, key]);

  const current = status.key === key;
  const retry = useCallback(() => setAttempt(value => value + 1), []);
  return {
    account: enabled && current && accountId !== null && accountIdentity(status.account?.id) === accountId ? status.account : null,
    loading: enabled && (!ready || (accountId !== null && (!current || status.loading))),
    error: enabled && current && status.error,
    retry,
  };
}
